import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const listItemVariants = cva(
  'flex w-full cursor-pointer items-center gap-3 border-0 border-b border-border/70 text-left shadow-none transition last:border-b-0',
  {
    variants: {
      size: {
        sm: 'min-h-8 px-4 py-1.5',
        md: 'min-h-12 px-3 py-3',
        lg: 'min-h-20 items-start px-4 py-3 overflow-hidden',
      },
      selected: {
        true: 'bg-primary/10 text-foreground',
        false: 'bg-transparent text-foreground hover:bg-accent/50',
      },
    },
    defaultVariants: { size: 'md', selected: false },
  },
);

export interface ListItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof listItemVariants> {}

const ListItem = React.forwardRef<HTMLButtonElement, ListItemProps>(
  ({ className, size, selected, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(listItemVariants({ size, selected }), className)}
      {...props}
    />
  ),
);
ListItem.displayName = 'ListItem';

export interface ListItemEmailProps
  extends Omit<ListItemProps, 'size' | 'children'> {
  title: string;
  subtitle: string;
  description?: string;
  timestamp?: string;
  trailing?: React.ReactNode;
  footer?: React.ReactNode;
}

const ListItemEmail = React.forwardRef<HTMLButtonElement, ListItemEmailProps>(
  ({ title, subtitle, description, timestamp, trailing, footer, ...props }, ref) => (
    <ListItem ref={ref} size="lg" {...props}>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2.5">
          <p className="min-w-0 flex-1 truncate text-sm font-medium leading-5">{title}</p>
          <div className="flex shrink-0 items-center gap-1.5 text-xs">
            {trailing}
            {timestamp ? (
              <span className="min-w-12 text-right text-muted-foreground">{timestamp}</span>
            ) : null}
          </div>
        </div>
        <p className="mt-0.5 truncate text-sm font-semibold leading-5">{subtitle}</p>
        {description ? (
          <p className="mt-0.5 truncate text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
        {footer}
      </div>
    </ListItem>
  ),
);
ListItemEmail.displayName = 'ListItemEmail';

export { ListItem, listItemVariants, ListItemEmail };
